'use client';

import React, { useRef, useEffect } from 'react';
import { Button } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useExtendedRouter, useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { useSwrEmployerBrandingActivate } from '@hooks/useSwrApi/endpoints/employer-branding/useSwrEmployerBrandingActivate';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { CheckIcon } from '@heroicons/react/16/solid';

export const EmployerBrandingBanner = () => {
  const { t } = useTranslation('common');
  const { locale, router } = useExtendedRouter();
  const { searchParamsAsObject } = useExtendedSearchParams();
  const { isAuth, redirectToLogin } = useAuthClient();
  const employerBrandingBannerRef = useRef<HTMLDivElement>(null);

  const { trigger: triggerActivate, isMutating: isActivating } = useSwrEmployerBrandingActivate({
    locale: locale || 'ro',
    onSuccess: () => {
      // success toast handled by hook; consider refreshing relevant data via external state if needed
    },
  });

  // Scroll to banner if scrollTo query param is present
  useEffect(() => {
    const scrollTo = searchParamsAsObject?.scrollTo;
    if (scrollTo === 'employer-branding' && employerBrandingBannerRef.current) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        employerBrandingBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete('scrollTo');
        const newRelativePathQuery = window.location.pathname + (newParams.toString() ? `?${newParams.toString()}` : '');

        // Use router.replace to update URL without refreshing or adding history entry
        router.replace(newRelativePathQuery, { scroll: false });
      }, 100);
    }
  }, [searchParamsAsObject, router]);

  const handleBuyClick = () => {
    // For unauthenticated users, redirect to login with callback to services page banner
    // We use query param instead of hash because hashes seems to be stripped after login
    if (!isAuth) {
      redirectToLogin('/company/offer?scrollTo=employer-branding');
    } else {
      // For authenticated users, attempt activation immediately.
      // If insufficient credits, backend returns 402 and the hook shows an error toast instructing to top up.
      void triggerActivate();
    }
  };

  return (
    <div ref={employerBrandingBannerRef} className="flex h-full flex-col justify-between">
      <div>
        <h3 className="mb-5 text-left font-bold">
          {t('offer.type6.name')}
        </h3>

        <div>
          <div className="mb-1 flex items-start gap-2">
            <CheckIcon className="size-5 min-w-5 text-secondary" />
            <span>{t('employer.branding_benefit1')}</span>
          </div>
          <div className="mb-1 flex items-start gap-2">
            <CheckIcon className="size-5 min-w-5 text-secondary" />
            <span>{t('employer.branding_benefit2')}</span>
          </div>
          <div className="mb-1 flex items-start gap-2">
            <CheckIcon className="size-5 min-w-5 text-secondary" />
            <span>{t('employer.branding_benefit3')}</span>
          </div>
          <div className="mb-1 flex items-start gap-2">
            <CheckIcon className="size-5 min-w-5 text-secondary" />
            <span>{t('employer.branding_benefit4')}</span>
          </div>
          <div className="mb-1 flex items-start gap-2">
            <CheckIcon className="size-5 min-w-5 text-secondary" />
            <span>{t('employer.branding_benefit5')}</span>
          </div>
        </div>
      </div>

      <div>
        <div className="mt-5 flex items-baseline gap-2">
          <span className="text-3xl font-bold md:text-4xl">
            1000
          </span>
          <span className="text-sm text-ink-medium">
            {t('employer.branding.price.desc')}
          </span>
        </div>

        <Button
          styling="solid"
          color="secondary"
          rounding="full"
          size="sm"
          className="mt-3 min-w-32"
          isLoading={isActivating}
          onClick={handleBuyClick}
        >
          {t('dashboard.label.buy_now')}
        </Button>
      </div>
    </div>
  );
};
